import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { DEMO_MODE } from '@/lib/demoMode';

/**
 * DB ground truth for public.app_role (see supabase/migrations
 * 20250928165422_*.sql + 20251008221831_*.sql / 20251008221854_*.sql, and
 * #894 slice 2a 20270221000000 which added 'marketing'):
 * 'admin' | 'moderator' | 'user' | 'super_admin' | 'operations_manager' |
 * 'customer_support' | 'finance_manager' | 'trust_safety_manager' | 'marketing'.
 */
export type AppRole =
  | 'super_admin'
  | 'operations_manager'
  | 'customer_support'
  | 'finance_manager'
  | 'trust_safety_manager'
  | 'admin'
  | 'moderator'
  | 'user'
  | 'marketing';

/**
 * Admin-panel allowlist (issue #471). Mirrors public.is_admin_staff() exactly
 * (supabase/migrations/20251014210120_*.sql): admin, super_admin,
 * operations_manager, customer_support, finance_manager, trust_safety_manager.
 *
 * Deliberately EXCLUDES:
 *  - 'user' — the default/no-privilege role. A user_roles row with role='user'
 *    must never grant admin-panel access (this was the #471 critical bug:
 *    isAdmin was derived from "any row exists", not from role identity).
 *  - 'moderator' — a real app_role value, but is_admin_staff() does not grant
 *    it server-side access to any admin-gated table today. Treating it as
 *    non-staff here keeps the client check a strict mirror of the server
 *    boundary; if moderator gains a defined admin scope later, add it to
 *    BOTH this list and is_admin_staff() together so they cannot drift.
 *  - 'marketing' (#894) — intentionally NOT added here. Console admission for
 *    the new 5-role model comes from allowed_admin_routes() (having >=1 route,
 *    e.g. 'index') at the cutover, NOT from is_admin_staff()/this legacy list —
 *    adding it here would grant the broad is_admin_staff backend surface it must
 *    never have. Wired in the cutover slice, not here.
 */
export const STAFF_ROLES = [
  'super_admin',
  'operations_manager',
  'customer_support',
  'finance_manager',
  'trust_safety_manager',
  'admin',
] as const;

export type StaffRole = (typeof STAFF_ROLES)[number];

export function isStaffRole(role: string): role is StaffRole {
  return (STAFF_ROLES as readonly string[]).includes(role);
}

export interface AdminRole {
  id: string;
  role: AppRole;
  created_at: string | null;
}

// ---------------------------------------------------------------------------
// Module-level cache: deduplicate user_roles / admin_setup fetches when
// multiple components call useAdminAuth() simultaneously.
// ---------------------------------------------------------------------------
let _rolesCache: { roles: AdminRole[]; ts: number } | null = null;
let _setupCache: { value: boolean; ts: number } | null = null;
let _cachedUserId: string | null = null;
const CACHE_TTL = 30_000; // 30 seconds

export const useAdminAuth = () => {
  const [isAdmin, setIsAdmin] = useState(DEMO_MODE);
  const [adminRoles, setAdminRoles] = useState<AdminRole[]>(
    DEMO_MODE
      ? [{ id: 'demo-role-1', role: 'super_admin', created_at: new Date().toISOString() }]
      : []
  );
  const [setupComplete, setSetupComplete] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  // Reads the app-wide module store — this does not open a second
  // onAuthStateChange subscription (see the perf-fix note on the effect below).
  const { user: authUser, degraded } = useAuth();
  const authUserId = authUser?.id ?? null;


  const checkAdminStatus = async (userId: string) => {
    try {
      // Invalidate cache when active user changes (e.g. rapid account-switch)
      if (userId !== _cachedUserId) {
        _rolesCache = null;
        _setupCache = null;
        _cachedUserId = userId;
      }

      // Use cache if fresh
      const now = Date.now();
      if (_rolesCache && now - _rolesCache.ts < CACHE_TTL) {
        const staff = _rolesCache.roles.some(r => isStaffRole(r.role));
        setIsAdmin(staff);
        setAdminRoles(_rolesCache.roles);
        return staff;
      }

      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('id, role, created_at')
        .eq('user_id', userId);

      if (error) throw error;

      _rolesCache = { roles: roles || [], ts: Date.now() };
      // #471: isAdmin is allowlist-derived (STAFF_ROLES), not presence-based.
      // A user_roles row whose role is 'user' (or any non-staff role) must
      // NOT grant admin-panel access.
      const isStaff = (roles || []).some(r => isStaffRole(r.role));
      setIsAdmin(isStaff);
      setAdminRoles(roles || []);
      return isStaff;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  };

  const checkSetupStatus = async () => {
    try {
      // Use cache if fresh
      const now = Date.now();
      if (_setupCache && now - _setupCache.ts < CACHE_TTL) {
        setSetupComplete(_setupCache.value);
        return _setupCache.value;
      }

      // Bug 5 fix: use maybeSingle() instead of single() to avoid HTTP 406
      const { data, error } = await supabase
        .from('admin_setup')
        .select('setup_completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn('admin_setup unavailable:', error.message);
        setSetupComplete(false);
        return false;
      }

      const val = data?.setup_completed || false;
      _setupCache = { value: val, ts: Date.now() };
      setSetupComplete(val);
      return val;
    } catch (error) {
      console.warn('Error checking setup status:', error);
      setSetupComplete(false);
      return false;
    }
  };

  const hasRole = (role: AdminRole['role']) => {
    return adminRoles.some(r => r.role === role);
  };

  const isSuperAdmin = () => {
    return hasRole('super_admin');
  };

  useEffect(() => {
    if (DEMO_MODE) {
      setLoading(false);
      return;
    }

    // While the global auth store is still resolving, getUser() can answer
    // "nobody" for a user who is in fact signed in — and with an empty
    // dependency array this effect would never re-run to correct itself, so a
    // staff member got a permanent "not an admin" verdict until a full page
    // reload. Stay in the loading state instead; `degraded` is bounded by
    // AUTH_RESOLVE_GRACE, after which it clears and this re-runs either way.
    if (degraded) return;

    const initializeAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        await checkAdminStatus(user.id);
      }

      await checkSetupStatus();
      setLoading(false);
    };

    initializeAuth();

    // perf-fix: removed duplicate onAuthStateChange listener.
    // The global useAuth() hook already manages session state app-wide.
    // Re-registering here caused 2-3x user_roles queries per page on admin routes.
    // Admin role is now cached at module level (CACHE_TTL=30s).
    //
    // Deps are the resolution signals, not a re-subscription: `degraded` so the
    // verdict is re-taken once auth settles, and the user id so an account
    // switch re-checks rather than serving the previous account's verdict.
  }, [degraded, authUserId]);

  const signOut = async () => {
    try {
      // Clear session storage
      sessionStorage.clear();
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear admin state
      setIsAdmin(false);
      setAdminRoles([]);
      
      // Clear module-level caches on sign out
      _rolesCache = null;
      _setupCache = null;
      _cachedUserId = null;
      
      // Show success message
      toast({
        title: 'Signed out',
        description: 'You have been successfully signed out.',
      });
      
      // Navigate to logout page for clean redirect
      navigate('/logout', { replace: true });
    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        variant: 'destructive',
        title: 'Sign out failed',
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return {
    isAdmin,
    adminRoles,
    setupComplete,
    loading,
    checkAdminStatus,
    checkSetupStatus,
    hasRole,
    isSuperAdmin,
    signOut,
  };
};
